import type { ChangeStateType } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";

import { formatPercentageWithDynamicPrecision } from "../website-keywords/table/changeValueFormatters";

import type {
    IKeywordsData,
    IKeywordsResponse,
    ITopicsData,
    ITopicsResponse,
    ITransformedEnrichedKeywordsData,
    ITransformedTopics,
} from "./types";

const NO_POSITION = -1;

export const transformTopicsData = (data: ITopicsResponse): ITransformedTopics => {
    const transformedData = data?.Data?.map((item: ITopicsData, index: number) => ({
        ...item,
        key: index,
        ClicksChange: {
            change: item.ClicksChange,
            changeState: null,
            changeValueFormatted: formatPercentageWithDynamicPrecision(item.ClicksChange),
        },
        ClicksShare: {
            clicksShare: item.ClicksShare,
            totalClicks: item.TotalClicks,
        },
    }));

    return {
        // @ts-ignore FIXME: Type 'null' is not assignable to type 'ChangeStateType'
        data: transformedData,
        totalCount: data?.TotalCount ?? 0,
    };
};

export const transformEnrichedKeywordsData = (
    data: IKeywordsResponse,
    domain: string,
): ITransformedEnrichedKeywordsData[] => {
    return data?.Data?.map((item: IKeywordsData, index: number) => ({
        ...item,
        key: index,
        Position: [{ Value: item.Position ?? NO_POSITION }],
        ClicksShare: { clicksShare: item.ClicksShare, totalClicks: item.TotalClicks },
        ClicksChange: {
            change: item.Change,
            changeState: item.ChangeState as ChangeStateType,
            changeValueFormatted: formatPercentageWithDynamicPrecision(item.Change),
        },
        positionChange: {
            Position: item.Position,
            PreviousPosition: item.PreviousPosition,
            PositionChange: item.PositionChange,
        },
        serpFeatures: {
            SearchTerm: item.Keyword,
            SerpFeatures: item.KeywordSerpFeatures,
            SiteSerpFeatures: { [domain]: item.WebsiteSerpFeatures },
        },
        Volume: getVolumeValue(item.Volume),
    }));
};

export const buildPageFilterFromKey = (key: string): string => {
    const trimmedKey = key.trim();
    if (!key || typeof key !== "string" || !trimmedKey) {
        return "";
    }

    try {
        const pageFilter = [
            {
                url: trimmedKey,
                searchType: "domain" as const,
            },
        ];

        return transformSiteListToParam(pageFilter);
    } catch (error) {
        Sentry.error("Failed to build page filter from key", error as Error, {
            extra: { key: trimmedKey },
        });
        return "";
    }
};

export const isPageFilterMatchingKey = (pageFilter: string, key: string): boolean => {
    if (!pageFilter || !key) {
        return false;
    }

    try {
        const parsedFilter = JSON.parse(pageFilter) as Array<{ url: string; searchType: string }>;
        if (!Array.isArray(parsedFilter) || parsedFilter.length === 0) {
            return false;
        }

        return parsedFilter.some((item) => item.url === key.trim());
    } catch {
        return false;
    }
};
